import { Card } from "@/components/layout";
import {
  MetricKpiTile,
  PanelError,
  PanelLoading,
  SectionHeading,
  panelErrorTitle,
} from "@/components/dashboard";
import { DonutChart, HorizontalBarList } from "@/components/charts";
import { useAuthMetrics } from "@/hooks/useAuthMetrics";
import { pickRateTone } from "@/lib/thresholds";
import type { TimeRange } from "@/lib/time-range";

interface AuthenticationSectionProps {
  range: TimeRange;
}

function fmtPct(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return "—";
  return `${n.toFixed(1)}%`;
}

export function AuthenticationSection({ range }: AuthenticationSectionProps) {
  const auth = useAuthMetrics(range);
  const a = auth.data;
  const authErr = auth.error ? panelErrorTitle(auth.error) : undefined;

  return (
    <section className="flex flex-col gap-3">
      <SectionHeading
        title="Authentication"
        frs={["FR-06"]}
        source="log-analytics"
        description="Login attempts vs success / failure (Auth0)."
      />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <MetricKpiTile
          label="Login attempts"
          value={a ? a.attempts.toLocaleString("en-US") : "—"}
          delta="login_attempted events"
          tone="neutral"
          hint="Number of times users tried to sign in via Auth0 in the selected range."
          isLoading={auth.isLoading}
          errorTitle={authErr}
        />
        <MetricKpiTile
          label="Login success rate"
          value={fmtPct(a?.successRatePct)}
          delta={a ? `${a.succeeded} / ${a.attempts}` : undefined}
          tone={pickRateTone(a?.successRatePct, { good: 99, warn: 95 })}
          hint="Share of sign-in attempts that succeeded."
          isLoading={auth.isLoading}
          errorTitle={authErr}
        />
        <MetricKpiTile
          label="Login failure rate"
          value={fmtPct(a?.failureRatePct)}
          delta={a ? `${a.failed} / ${a.attempts}` : undefined}
          tone={
            a?.failureRatePct === undefined
              ? "neutral"
              : a.failureRatePct <= 1
              ? "emerald"
              : a.failureRatePct <= 5
              ? "amber"
              : "red"
          }
          hint="Share of sign-in attempts that failed. High values usually mean bad credentials or an outage."
          isLoading={auth.isLoading}
          errorTitle={authErr}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Login outcomes">
          {auth.isLoading ? (
            <PanelLoading />
          ) : auth.error ? (
            <PanelError error={auth.error} onRetry={() => auth.refetch()} />
          ) : (
            <DonutChart
              slices={[
                {
                  label: "Succeeded",
                  value: a?.succeeded ?? 0,
                  tone: "emerald",
                },
                { label: "Failed", value: a?.failed ?? 0, tone: "red" },
              ]}
              centerLabel={
                a ? `${a.attempts.toLocaleString("en-US")} attempts` : undefined
              }
            />
          )}
        </Card>

        <Card title="Failure breakdown by errorCode">
          {auth.isLoading ? (
            <PanelLoading />
          ) : auth.error ? (
            <PanelError error={auth.error} onRetry={() => auth.refetch()} />
          ) : (
            <HorizontalBarList
              entries={Object.entries(a?.failuresByErrorCode ?? {}).map(
                ([label, value]) => ({ label, value }),
              )}
              tone="red"
              emptyMessage="No login failures in the selected range"
            />
          )}
        </Card>
      </div>
    </section>
  );
}
